if (!customElements.get('subscription-popup')) {
  /* Counting is per page load, not per element. Guards against a double count
     if the section is re-rendered (section rendering API, editor reloads). */
  let visitCounted = false;

  const KEY_VIEWS = 'niam:session-views';
  const KEY_PRODUCT_SEEN = 'niam:seen-product';
  const KEY_SUBSCRIBED = 'niam:subscribed';

  /* Templates where the popup stays shut. Cart and checkout are mid-purchase,
     account pages already know who they are, and 404/password are dead ends.
     The pageview still counts on all of them. */
  const MUTED_TEMPLATES = ['cart', '404', 'password', 'challenge', 'gift_card'];

  /* Storage throws, not returns null, in Safari private mode. */
  const store = {
    get(area, key) {
      try {
        return window[area].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(area, key, value) {
      try {
        window[area].setItem(key, value);
      } catch (e) {
        /* no-op */
      }
    },
  };

  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.template = this.dataset.template || '';
        this.minPageviews = parseInt(this.dataset.minPageviews, 10) || 2;
        this.requireProductView = this.dataset.requireProductView === 'true';
        this.settleDelay = (parseInt(this.dataset.settleDelay, 10) || 5) * 1000;
        this.scrollQualifier = 0.25;
        this.cookieName = 'sleektheme:popup';

        this.trackVisit();

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });
      }

      /* ------------------------------------------------------------ session */

      trackVisit() {
        if (Shopify.designMode) return;

        if (this.template === 'product') store.set('sessionStorage', KEY_PRODUCT_SEEN, '1');

        if (visitCounted) {
          this.pageviews = parseInt(store.get('sessionStorage', KEY_VIEWS), 10) || 1;
          return;
        }
        visitCounted = true;

        const next = (parseInt(store.get('sessionStorage', KEY_VIEWS), 10) || 0) + 1;
        store.set('sessionStorage', KEY_VIEWS, String(next));
        this.pageviews = next;
      }

      /** True on the page load immediately after a successful signup. */
      justSubscribed() {
        try {
          return new URLSearchParams(window.location.search).get('customer_posted') === 'true';
        } catch (e) {
          return false;
        }
      }

      isSubscribed() {
        return store.get('localStorage', KEY_SUBSCRIBED) === '1';
      }

      /* --------------------------------------------------------------- open */

      handleOpen() {
        if (Shopify.designMode) return;

        /* They just gave us the email. Pay out the code and never ask again. */
        if (this.justSubscribed()) {
          store.set('localStorage', KEY_SUBSCRIBED, '1');
          if (this.querySelector('.subscription-popup__reward')) this.showReward();
          return;
        }

        if (this.isSubscribed()) return;

        const savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) return;

        switch (this.triggerOpen) {
          case 'engaged':
            this.triggerWhenEngaged();
            break;
          case 'delay':
            setTimeout(() => {
              this.show();
            }, 5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
          default:
            this.show();
            break;
        }
      }

      /**
       * The serious-shopper trigger.
       *
       * Two gates. First, the visitor has to have crossed the browsing
       * threshold this session, which means a bounce never sees it. Second,
       * they have to settle on the qualifying page: either dwell for
       * settleDelay or scroll a quarter of the way down, whichever lands
       * first. A page they open and immediately close does not count.
       */
      triggerWhenEngaged() {
        if (MUTED_TEMPLATES.includes(this.template)) return;
        if (this.template.indexOf('customers/') === 0) return;
        if (this.pageviews < this.minPageviews) return;
        if (this.requireProductView && !store.get('sessionStorage', KEY_PRODUCT_SEEN)) return;

        let fired = false;

        const open = () => {
          if (fired) return;
          fired = true;
          clearTimeout(timer);
          window.removeEventListener('scroll', onScroll);
          this.show();
        };

        const onScroll = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && window.scrollY / max > this.scrollQualifier) open();
        };

        const timer = setTimeout(open, this.settleDelay);
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      showReward() {
        this.classList.add('is-success');
        this.show();

        /* Strip the flag so a refresh or a shared URL does not replay it. */
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('customer_posted');
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          /* no-op */
        }
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
